import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Box, Skeleton, Stack } from '@mui/material';
import { influencers, InfluencerTypes } from 'api';
import { getChartOptions } from 'helpers/chartHelpers';
import { InfoCard } from 'components';
import { colors } from 'theme/constants';
import Chart from '../ComparisonChart/Chart';

interface Props {
  data: InfluencerTypes.ItemExtended;
}

const DealsChart = (props: Props): JSX.Element | null => {
  const { data: influencerData } = props;

  const influencerId = useMemo(
    () => influencerData?._id?.$oid,
    [influencerData],
  );

  const { data, isLoading } = useQuery(
    [influencers.endpoints.getDealsChartById(influencerId), 'budget_revenue'],
    () => influencers.getDealsChartById(influencerId, 'budget_revenue'),
  );

  const chartData = useMemo(() => {
    if (data) {
      return getChartOptions({
        key: 'budget_revenue',
        chartData: data,
        avgData: 'global_avg_details' in data ? data.global_avg_details : {},
      });
    }

    return [];
  }, [data]);

  const budgetRevenueChartConfig = {
    primary: { name: 'Total Budget', dataKey: 'budget' },
    secondary: { name: 'Total Revenue', dataKey: 'revenue' },
  };

  return (
    <InfoCard title="Budget & Revenue" color={colors.pink1}>
      <Stack p={3}>
        {isLoading ? (
          <Box mt={3}>
            <Skeleton
              variant="rectangular"
              height={`${400 - 24}px`}
              sx={(t) => ({ borderRadius: `${t.shape.borderRadius}px` })}
            />
          </Box>
        ) : (
          <Chart
            variant="line"
            height={400}
            data={chartData}
            chartDataConfig={budgetRevenueChartConfig}
          />
        )}
      </Stack>
    </InfoCard>
  );
};

export default DealsChart;
